import { Button, Flex, Text } from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";
import { QuestionAnswerRow } from "./QuestionAnswerRow";
import * as styles from "./styles_vanilla.css";
import type { ProdOpsResponseQuestion } from "./QuestionAnswerRow";
import type { GetDdqResponseForProdOpsQuery } from "~/graphql/supergraphTypes";

export type ProdOpsResponseSection =
  GetDdqResponseForProdOpsQuery["ddqResponseForProdOps"]["sections"][number];

export type SectionGroupProps = {
  arenaId: number;
  responseId: string;
  section: ProdOpsResponseSection;
  allSections: ProdOpsResponseSection[];
  allQuestions: ProdOpsResponseQuestion[];
  depth: number;
  canRerun: boolean;
  onRerunSection: (sectionId: string, sectionTitle: string) => void;
};

// Re-running a section already covers its nested children server-side, so this only passes the
// clicked section's own id — the recursion here is purely for rendering.
export const SectionGroup = ({
  arenaId,
  responseId,
  section,
  allSections,
  allQuestions,
  depth,
  canRerun,
  onRerunSection,
}: SectionGroupProps) => {
  const childSections = allSections
    .filter((candidate) => candidate.parentSectionId === section.id)
    .sort((a, b) => a.position[a.position.length - 1] - b.position[b.position.length - 1]);
  const ownQuestions = allQuestions
    .filter((question) => question.sectionId === section.id)
    .sort((a, b) => a.position[a.position.length - 1] - b.position[b.position.length - 1]);

  return (
    <Flex flexDirection="column" gap={SPACING.SMALL} className={styles.sectionGroup}>
      <Flex justifyContent="space-between" alignItems="center">
        <Text variant={depth === 0 ? "h4" : "strong-md"}>{section.title}</Text>
        <Button
          variant="link"
          displayAsLink
          isInline
          disabled={!canRerun}
          onClick={() => onRerunSection(section.id, section.title)}
          data-testid={`ddq-answer-qa-rerun-section-${section.id}`}
        >
          Re-run section
        </Button>
      </Flex>

      {ownQuestions.map((question) => (
        <QuestionAnswerRow
          key={question.id}
          arenaId={arenaId}
          responseId={responseId}
          question={question}
        />
      ))}

      {childSections.map((child) => (
        <SectionGroup
          key={child.id}
          arenaId={arenaId}
          responseId={responseId}
          section={child}
          allSections={allSections}
          allQuestions={allQuestions}
          depth={depth + 1}
          canRerun={canRerun}
          onRerunSection={onRerunSection}
        />
      ))}
    </Flex>
  );
};
